/**
 * NotificationPrompt — custom in-app banner that explains the benefit of push
 * notifications before triggering the browser's native permission dialog.
 *
 * Show conditions (all must be true):
 *   • Browser supports push (service worker + PushManager)
 *   • Permission hasn't been granted or denied yet
 *   • User hasn't dismissed this prompt in the last 7 days
 *   • User has completed at least one study session (words_known_count > 0)
 *
 * The banner appears at the bottom-center of the viewport, above the mobile
 * bottom nav (80 px offset).
 */
import { useEffect, useState } from 'react'
import { useAuthStore, selectUser } from '../../stores/authStore'
import { usePushNotifications } from '../../hooks/usePushNotifications'
import { useLocale } from '../../i18n/context'

export default function NotificationPrompt() {
  const user = useAuthStore(selectUser)
  const { isSupported, isLoading, subscribe, shouldShowPrompt, dismissPrompt } =
    usePushNotifications()
  const { tr } = useLocale()
  const notif = tr.notification

  const [visible, setVisible] = useState(false)

  useEffect(() => {
    // Show only for authenticated, onboarded users who have studied at least once
    if (!user?.onboarding_completed) return
    if ((user?.words_known_count ?? 0) === 0) return
    if (!shouldShowPrompt()) return
    setVisible(true)
  }, [user, shouldShowPrompt])

  if (!isSupported || !visible) return null

  const handleAllow = async () => {
    const ok = await subscribe()
    // Always hide — if permission was denied the browser won't let us ask again
    setVisible(false)
    if (!ok) dismissPrompt()
  }

  const handleDismiss = () => {
    dismissPrompt()
    setVisible(false)
  }

  return (
    <div
      role="dialog"
      aria-label={notif.ariaLabel}
      style={{
        position: 'fixed',
        bottom: 88, // clears mobile bottom nav (60px) + some breathing room
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'min(400px, calc(100vw - 32px))',
        backgroundColor: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        padding: '20px 20px 16px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        zIndex: 200,
        animation: 'notifPromptSlideUp 0.25s ease both',
      }}
    >
      <style>{`
        @keyframes notifPromptSlideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(16px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
        <span style={{ fontSize: 28, lineHeight: 1, flexShrink: 0 }}>🔔</span>
        <div>
          <p style={{ margin: 0, fontWeight: 600, fontSize: 15, color: 'var(--text-primary)', marginBottom: 4 }}>
            {notif.title}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {notif.desc}
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={handleAllow}
          disabled={isLoading}
          style={{
            flex: 1,
            backgroundColor: 'var(--accent-fill)',
            color: 'var(--text-on-accent)',
            border: 'none',
            borderRadius: 8,
            padding: '10px 0',
            fontWeight: 600,
            fontSize: 14,
            cursor: isLoading ? 'wait' : 'pointer',
            opacity: isLoading ? 0.7 : 1,
            transition: 'opacity 0.15s',
            minHeight: 44,
          }}
        >
          {isLoading ? notif.enabling : notif.enableBtn}
        </button>
        <button
          onClick={handleDismiss}
          disabled={isLoading}
          style={{
            flex: 1,
            backgroundColor: 'transparent',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            padding: '10px 0',
            fontWeight: 500,
            fontSize: 14,
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          {notif.notNow}
        </button>
      </div>
    </div>
  )
}
